import React, { useEffect, useRef } from 'react';
import {
  Alert, Button, Progress, Table,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { IAssetIconBatchResult } from '../../../../utils/asset-type-import/i-import-job';
import { MAX_ICONS_PER_RUN } from '../../../../utils/asset-type-import/constants';
import { IIconsStepProps } from './i-import-wizard';
import {
  BODY_CELL, CountTag, ERROR_TAG, GREY_TAG, HEADER_CELL, MUTED_TEXT, OK_TAG,
} from './import-tags';

/**
 * Optional icons, matched to asset types by file name. [AVP-891]
 *
 * The browser used to pre-match names against the codes the import had just
 * created, but with a server-side job it no longer holds that list. Matching
 * happens on the server instead, so "unknown assetType code" arrives as a
 * per-entry result rather than a guess made up front.
 */

export const supportsDirectoryPicker = (): boolean => (
  typeof HTMLInputElement !== 'undefined' && 'webkitdirectory' in HTMLInputElement.prototype
);

const resultColumns: ColumnsType<IAssetIconBatchResult> = [
  {
    title: 'Code',
    dataIndex: 'code',
    key: 'code',
    width: 220,
    onHeaderCell: () => ({ style: HEADER_CELL }),
    onCell: () => ({ style: BODY_CELL }),
  },
  {
    title: 'Result',
    dataIndex: 'status',
    key: 'status',
    width: 120,
    onHeaderCell: () => ({ style: HEADER_CELL }),
    onCell: () => ({ style: BODY_CELL }),
  },
  {
    title: 'Reason',
    dataIndex: 'message',
    key: 'message',
    render: (reason?: string) => reason ?? '',
    onHeaderCell: () => ({ style: HEADER_CELL }),
    onCell: () => ({ style: BODY_CELL }),
  },
];

const rejectedColumns: ColumnsType<{ fileName: string; reason: string }> = [
  {
    title: 'File',
    dataIndex: 'fileName',
    key: 'fileName',
    width: 260,
    onHeaderCell: () => ({ style: HEADER_CELL }),
    onCell: () => ({ style: BODY_CELL }),
  },
  {
    title: 'Why it was not sent',
    dataIndex: 'reason',
    key: 'reason',
    onHeaderCell: () => ({ style: HEADER_CELL }),
    onCell: () => ({ style: BODY_CELL }),
  },
];

const IconsStep = (props: IIconsStepProps): JSX.Element => {
  const {
    phase, files, rejected, progress, results, supportsDirectory, disabled, error,
    onFilesSelected, onZipSelected,
  } = props;

  const filesInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const zipInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // not a documented React prop; it has to be set on the node
    if (folderInput.current) {
      folderInput.current.setAttribute('webkitdirectory', '');
      folderInput.current.setAttribute('directory', '');
    }
  }, [supportsDirectory]);

  const pick = (ref: React.RefObject<HTMLInputElement>): void => {
    const input = ref.current;
    if (input) {
      // so re-picking the same selection still fires change
      input.value = '';
      input.click();
    }
  };

  const handleFiles = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onFilesSelected([...(event.target.files ?? [])]);
  };

  const handleZip = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const zip = event.target.files?.[0];
    if (zip) {
      onZipSelected(zip);
    }
  };

  const created = results.filter((row) => row.status === 'created').length;
  const updated = results.filter((row) => row.status === 'updated').length;
  const failed = results.filter((row) => row.status === 'failed').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ fontSize: 13 }}>
        Optional. Icons are matched to asset types by file name, so
        {' '}
        <code>SKU900.png</code>
        {' '}
        becomes the icon for code
        {' '}
        <code>SKU900</code>
        .
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button disabled={disabled || phase === 'uploading'} onClick={() => pick(filesInput)}>
          Choose files
        </Button>
        {supportsDirectory ? (
          <Button
            disabled={disabled || phase === 'uploading'}
            onClick={() => pick(folderInput)}
          >
            Choose folder
          </Button>
        ) : null}
        <Button disabled={disabled || phase === 'uploading'} onClick={() => pick(zipInput)}>
          Choose a .zip
        </Button>
      </div>

      <input
        ref={filesInput}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        onChange={handleFiles}
      />
      {supportsDirectory ? (
        <input
          ref={folderInput}
          type="file"
          multiple
          style={{ display: 'none' }}
          onChange={handleFiles}
        />
      ) : null}
      <input
        ref={zipInput}
        type="file"
        accept=".zip,application/zip"
        style={{ display: 'none' }}
        onChange={handleZip}
      />

      {error ? <Alert type="error" showIcon message={error} /> : null}

      {files.length > 0 || rejected.length > 0 ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <CountTag count={files.length} label="ready to send" tone={OK_TAG} />
          {rejected.length > 0
            ? <CountTag count={rejected.length} label="will not be sent" tone={GREY_TAG} />
            : null}
        </div>
      ) : null}

      {phase === 'uploading' ? (
        <>
          <Progress
            percent={progress.total > 0
              ? Math.round((progress.done / progress.total) * 100)
              : 0}
            status="active"
          />
          <div style={MUTED_TEXT}>{`Uploading ${progress.done} of ${progress.total}…`}</div>
        </>
      ) : null}

      {phase === 'done' ? (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <CountTag count={created} label="added" tone={OK_TAG} />
          {updated > 0 ? <CountTag count={updated} label="replaced" tone={OK_TAG} /> : null}
          {failed > 0 ? <CountTag count={failed} label="failed" tone={ERROR_TAG} /> : null}
        </div>
      ) : null}

      {rejected.length > 0 ? (
        <div style={{ border: '1px solid #f1f2f5', borderRadius: 11, overflow: 'hidden' }}>
          <Table
            size="small"
            columns={rejectedColumns}
            dataSource={rejected}
            rowKey={(row) => row.fileName}
            bordered={false}
            pagination={{ pageSize: 5, size: 'small', hideOnSinglePage: true }}
          />
        </div>
      ) : null}

      {failed > 0 ? (
        <div style={{ border: '1px solid #f1f2f5', borderRadius: 11, overflow: 'hidden' }}>
          <Table
            size="small"
            columns={resultColumns}
            dataSource={results.filter((row) => row.status === 'failed')}
            rowKey={(row, index) => `${row.code}-${index ?? 0}`}
            bordered={false}
            pagination={{ pageSize: 5, size: 'small', hideOnSinglePage: true }}
          />
        </div>
      ) : null}

      <div style={MUTED_TEXT}>
        {`PNG, JPEG or WebP up to 512 KB, and up to ${MAX_ICONS_PER_RUN} icons per run. `}
        The server resizes every icon to a 256×256 PNG. For more than that, script
        {' '}
        <code>POST /asset-manager/api/assetIcons/batch</code>
        .
      </div>
    </div>
  );
};

IconsStep.displayName = 'IconsStep';

export default IconsStep;
